import React from "react";
import Button, { ButtonDesign } from "../Button/Button";
import Icon, { IconSize } from "../Icon/Icon";
import styles from "./ConfirmDialog.module.css";
import { view } from "../../lib/vvm";
import { ConfirmDialogViewModel } from "./ConfirmDialogViewModel";

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  description?: string;
  reasonPrompt?: string;
  reasonPlaceholder?: string;
  reasonRequired?: boolean;
  reasonMinLength?: number;
  reasonMaxLength?: number;
  confirmLabel?: string;
  cancelLabel?: string;
  isProcessing?: boolean;
  tone?: "danger" | "neutral";
  onConfirm: (reason?: string | null) => void | Promise<void>;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = view(
  ConfirmDialogViewModel,
)<ConfirmDialogProps>(({
  viewModel: {
    isOpen,
    title,
    titleId,
    description,
    hasDescription,
    ariaDescriptionId,
    confirmLabel,
    cancelLabel,
    isProcessing,
    tone,
    showReasonInput,
    reasonLabel,
    reasonPlaceholder,
    reasonValue,
    reasonError,
    reasonCounterLabel,
    reasonMaxLength,
    canConfirm,
    onReasonChange,
    onOverlayMouseDown,
    onDialogMouseDown,
    onCancelClick,
    onConfirmClick,
  },
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.overlay} onMouseDown={onOverlayMouseDown}>
      <div
        className={`${styles.dialog} ${
          tone === "danger" ? styles.dialogDanger : styles.dialogNeutral
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={ariaDescriptionId}
        onMouseDown={onDialogMouseDown}
      >
        <div className={styles.header}>
          <span className={styles.iconWrap} aria-hidden="true">
            <Icon
              name={tone === "danger" ? "Delete" : "Info"}
              size={IconSize.medium}
            />
          </span>
          <h3 id={titleId} className={styles.title}>
            {title}
          </h3>
        </div>
        {hasDescription && (
          <p id={ariaDescriptionId} className={styles.description}>
            {description}
          </p>
        )}
        {showReasonInput && (
          <div className={styles.reasonBlock}>
            <label className={styles.reasonLabel} htmlFor={titleId + "-reason"}>
              {reasonLabel}
            </label>
            <textarea
              id={titleId + "-reason"}
              className={`${styles.reasonInput} ${
                reasonError ? styles.reasonInputError : ""
              }`}
              value={reasonValue}
              onChange={onReasonChange}
              placeholder={reasonPlaceholder}
              maxLength={reasonMaxLength}
              disabled={isProcessing}
              rows={3}
            />
            <div className={styles.reasonMeta}>
              <span className={styles.reasonError}>{reasonError || ""}</span>
              <span className={styles.reasonCounter}>{reasonCounterLabel}</span>
            </div>
          </div>
        )}
        <div className={styles.actions}>
          <Button
            design={ButtonDesign.basic}
            className={styles.actionButton}
            onClick={onCancelClick}
            disabled={isProcessing}
          >
            {cancelLabel}
          </Button>
          <Button
            design={ButtonDesign.basic}
            className={`${styles.actionButton} ${
              tone === "danger"
                ? styles.confirmButtonDanger
                : styles.confirmButtonNeutral
            }`}
            onClick={onConfirmClick}
            isLoading={isProcessing}
            disabled={isProcessing || !canConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
});
